import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ListChecks, Search, ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import logger from '@/lib/logger';

const STAGE_LABELS = {
  documents_pending: 'Docs Pending', documents_received: 'Docs Received',
  further_docs_required: 'Further Docs Required', ready_for_review: 'Review Pending',
  tax_payable: 'Tax Payable', ready_to_file: 'Ready to File',
};

const STAGE_COLORS = {
  documents_pending: 'bg-amber-100 text-amber-700',
  documents_received: 'bg-blue-100 text-blue-700',
  further_docs_required: 'bg-orange-100 text-orange-700',
  ready_for_review: 'bg-purple-100 text-purple-700',
  tax_payable: 'bg-rose-100 text-rose-700',
  ready_to_file: 'bg-indigo-100 text-indigo-700',
};

const getMonthLabel = (period) => {
  if (!period || period === '-') return '-';
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [yr, m] = period.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(m) - 1]}'${yr.slice(2)}`;
  }
  const match = period.match(/^([a-z]+)_(\d{4})$/i);
  if (match) {
    const mNames = { jan:'Jan', feb:'Feb', mar:'Mar', apr:'Apr', may:'May', jun:'Jun', jul:'Jul', aug:'Aug', sep:'Sep', oct:'Oct', nov:'Nov', dec:'Dec' };
    return `${mNames[match[1].toLowerCase()] || match[1]}'${match[2].slice(2)}`;
  }
  return period;
};

export default function OpenTasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [statusReport, setStatusReport] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'client_name', dir: 'asc' });
  const [staffFilter, setStaffFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.subtaskStatusReport()
      .then(res => setStatusReport(res.data))
      .catch(err => logger.error('Failed to load', err))
      .finally(() => setLoading(false));
  }, []);

  const allItems = useMemo(() => {
    if (!statusReport?.report) return [];
    const items = [];
    for (const [status, list] of Object.entries(statusReport.report)) {
      if (status === 'filed') continue;
      for (const item of list) items.push({ ...item, status });
    }
    return items;
  }, [statusReport]);

  const stageCounts = useMemo(() => {
    const counts = {};
    for (const item of allItems) {
      counts[item.status] = (counts[item.status] || 0) + 1;
    }
    return counts;
  }, [allItems]);

  const filteredItems = useMemo(() => {
    let items = allItems;
    if (stageFilter !== 'all') items = items.filter(i => i.status === stageFilter);
    if (clientFilter !== 'all') items = items.filter(i => i.client_id === clientFilter);
    if (boardFilter !== 'all') items = items.filter(i => i.board_id === boardFilter);
    if (staffFilter !== 'all') {
      if (staffFilter === '_unassigned') {
        items = items.filter(i => !i.assigned_to);
      } else {
        items = items.filter(i => i.assigned_to === staffFilter);
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i => i.client_name.toLowerCase().includes(q) || i.subtask_title.toLowerCase().includes(q));
    }
    const { col, dir } = sort;
    return [...items].sort((a, b) => {
      const va = (a[col] || '').toLowerCase();
      const vb = (b[col] || '').toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [allItems, stageFilter, clientFilter, boardFilter, staffFilter, searchQuery, sort]);

  const toggleSort = (col) => {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
        </Button>
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-orange-600" />
            Open Tasks
            <Badge variant="secondary" className="ml-1">{filteredItems.length}</Badge>
          </h2>
          <p className="text-sm text-muted-foreground">Tasks in progress — not yet filed</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${stageFilter === 'all' ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStageFilter('all')}>
          <p className="text-xl font-bold">{allItems.length}</p>
          <p className="text-[10px] font-medium text-muted-foreground">All</p>
        </div>
        {Object.entries(STAGE_LABELS).map(([key, label]) => (
          <div key={key} className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${stageFilter === key ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStageFilter(stageFilter === key ? 'all' : key)}>
            <p className="text-xl font-bold">{stageCounts[key] || 0}</p>
            <p className="text-[10px] font-medium text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-[280px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search client or subtask..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-8 text-xs" data-testid="open-tasks-search" />
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="open-tasks-client-filter"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {statusReport?.filters?.clients?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={boardFilter} onValueChange={setBoardFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="open-tasks-board-filter"><SelectValue placeholder="All Boards" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Boards</SelectItem>
            {statusReport?.filters?.boards?.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={staffFilter} onValueChange={setStaffFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="open-tasks-staff-filter"><SelectValue placeholder="All Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team Members</SelectItem>
            <SelectItem value="_unassigned">Unassigned</SelectItem>
            {statusReport?.filters?.staff?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || clientFilter !== 'all' || boardFilter !== 'all' || stageFilter !== 'all' || staffFilter !== 'all') && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setSearchQuery(''); setClientFilter('all'); setBoardFilter('all'); setStageFilter('all'); setStaffFilter('all'); }}>Clear</Button>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto">{filteredItems.length} item(s)</span>
      </div>

      {/* Table */}
      {filteredItems.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                {[
                  { key: 'client_name', label: 'Client' },
                  { key: 'subtask_title', label: 'Subtask' },
                  { key: 'board_name', label: 'Board' },
                  { key: 'period', label: 'Period' },
                  { key: 'staff_name', label: 'Assigned' },
                  { key: 'status', label: 'Stage' },
                ].map(col => (
                  <TableHead key={col.key} className="text-xs font-semibold cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort(col.key)} data-testid={`sort-${col.key}`}>
                    <span className={`flex items-center gap-1 ${sort.col === col.key ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {col.label}
                      {sort.col === col.key
                        ? (sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />)
                        : <ArrowUpDown className="h-3.5 w-3.5 opacity-70" />}
                    </span>
                  </TableHead>
                ))}
                <TableHead className="w-[30px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <TableRow key={`${item.subtask_id}-${item.client_id}-${item.period}`} className="hover:bg-muted/30 cursor-pointer group" onClick={() => navigate(`/dashboard/boards/${item.board_id}/task/${item.task_id}/subtask/${item.subtask_id}`)} data-testid={`open-task-row-${idx}`}>
                  <TableCell className="text-sm font-medium">{item.client_name}</TableCell>
                  <TableCell className="text-sm">{item.subtask_title}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                  <TableCell className="text-xs">{getMonthLabel(item.period)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.staff_name || '-'}</TableCell>
                  <TableCell><Badge variant="secondary" className={`text-[10px] ${STAGE_COLORS[item.status] || ''}`}>{STAGE_LABELS[item.status] || item.status}</Badge></TableCell>
                  <TableCell><ChevronRight className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No open tasks{stageFilter !== 'all' ? ` with "${STAGE_LABELS[stageFilter]}" status` : ''}</CardContent></Card>
      )}
    </div>
  );
}
